import { useState } from 'react';
import { Avatar } from '../ui';
import { formatDate, formatTime } from '../../lib/date-utils';

export interface Message {
  id: string;
  text: string | null;
  isOutbound: boolean;
  senderName: string | null;
  senderRole: 'client' | 'operator' | null;
  timestamp: string;
  // Send status (for outbound email messages)
  sendStatus?: 'sending' | 'sent' | 'failed';
  sendError?: string;
  // Phase 9: кто отправил (оператор или AI)
  sentBy?: 'operator' | 'ai';
}

export interface ShadowMatch {
  score: number;
  category: 'used' | 'partial' | 'not_used';
}

interface MessageBubbleProps {
  message: Message;
  shadowMatch?: ShadowMatch | null;
  onRetry?: ((messageId: string) => void) | undefined;
  /** Phase 8: коррекция AI ответа */
  onCorrect?: (messageId: string, correctedText: string) => void;
}

function getMatchColor(score: number): string {
  if (score >= 0.8) return 'text-accent-success bg-accent-success/10 border border-accent-success/20';
  if (score >= 0.45) return 'text-accent-warning bg-accent-warning/10 border border-accent-warning/20';
  return 'text-text-secondary bg-surface-subtle border border-border-subtle';
}

function getMatchBarColor(score: number): string {
  if (score >= 0.8) return 'bg-accent-success';
  if (score >= 0.45) return 'bg-accent-warning';
  return 'bg-text-muted';
}

export function MessageBubble({ message, shadowMatch, onRetry, onCorrect }: MessageBubbleProps): JSX.Element {
  const { isOutbound, senderName, senderRole, text, timestamp, sendStatus, sendError, sentBy } = message;
  const [showCorrection, setShowCorrection] = useState(false);
  const [correctionText, setCorrectionText] = useState(text ?? '');
  const displayName = isOutbound ? 'Вы' : (senderName ?? 'Пользователь');

  const containerStyles = [
    'flex gap-3 max-w-[85%]',
    isOutbound ? 'ml-auto flex-row-reverse' : '',
  ].join(' ');

  const bubbleStyles = [
    'rounded-2xl px-4 py-3',
    'break-words',
    isOutbound
      ? 'bg-surface-elevated border border-border-subtle shadow-sm rounded-tr-md'
      : 'bg-surface-subtle rounded-tl-md',
  ].join(' ');

  return (
    <div className={containerStyles}>
      <Avatar name={displayName} size="sm" />

      <div className={isOutbound ? 'text-right' : ''}>
        <div className="flex items-center gap-2 mb-1">
          {!isOutbound && (
            <>
              <span className="text-sm font-medium text-text-primary">
                {senderName ? `@${senderName}` : 'Пользователь'}
              </span>
              <span className="px-2 py-0.5 text-xs rounded-full bg-surface-subtle text-text-secondary border border-border-subtle">
                {senderRole === 'client' ? 'клиент' : senderRole ?? 'клиент'}
              </span>
            </>
          )}
          <span className="text-xs text-text-secondary">
            {formatDate(timestamp)}, {formatTime(timestamp)}
          </span>
          {isOutbound && (
            <>
              {sentBy === 'ai' ? (
                <span className="px-2 py-0.5 text-xs rounded-full bg-accent-ai/10 text-accent-ai border border-accent-ai/20">
                  AI ответил
                </span>
              ) : (
                <span className="px-2 py-0.5 text-xs rounded-full bg-accent-brand/10 text-accent-brand border border-accent-brand/20">
                  оператор
                </span>
              )}
              <span className="text-sm font-medium text-text-primary">
                {sentBy === 'ai' ? 'AI' : 'Вы'}
              </span>
            </>
          )}
        </div>

        <div className={bubbleStyles}>
          <p className="text-sm text-text-primary whitespace-pre-wrap">
            {text ?? '(пустое сообщение)'}
          </p>
        </div>

        {/* Send status indicator for outbound messages */}
        {isOutbound && sendStatus && (
          <div className="flex items-center gap-2 mt-1 justify-end">
            {sendStatus === 'sending' && (
              <span className="flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-accent-warning/10 text-accent-warning border border-accent-warning/20">
                <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Отправляется...
              </span>
            )}
            {sendStatus === 'sent' && (
              <span className="flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-accent-success/10 text-accent-success border border-accent-success/20">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                Отправлено
              </span>
            )}
            {sendStatus === 'failed' && (
              <>
                <span
                  className="flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-accent-danger/10 text-accent-danger border border-accent-danger/20 cursor-help"
                  title={sendError ?? 'Ошибка отправки'}
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Ошибка
                </span>
                {onRetry && (
                  <button
                    onClick={() => onRetry(message.id)}
                    className="flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-accent-brand/10 text-accent-brand border border-accent-brand/20 hover:bg-accent-brand/20 transition-colors"
                    title="Повторить отправку"
                  >
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Повторить
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {/* Shadow match indicator for operator messages */}
        {isOutbound && shadowMatch && (
          <div className="flex items-center gap-2 mt-1 justify-end">
            <span className={`px-2 py-0.5 text-xs rounded ${getMatchColor(shadowMatch.score)}`}>
              ↔ shadow: {Math.round(shadowMatch.score * 100)}%
            </span>
            <div className="w-12 h-1.5 rounded-full bg-surface-subtle overflow-hidden">
              <div
                className={`h-full rounded-full ${getMatchBarColor(shadowMatch.score)}`}
                style={{ width: `${shadowMatch.score * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Коррекция AI ответа */}
        {isOutbound && sentBy === 'ai' && onCorrect && !showCorrection && (
          <div className="mt-1 flex justify-end">
            <button
              onClick={() => setShowCorrection(true)}
              className="text-xs text-text-muted hover:text-accent-brand transition-colors"
            >
              Поправить ответ
            </button>
          </div>
        )}

        {showCorrection && onCorrect && (
          <div className="mt-2 p-2 rounded-comp bg-surface-subtle border border-border-default">
            <div className="text-xs text-text-secondary mb-1">Как лучше было ответить:</div>
            <textarea
              value={correctionText}
              onChange={(e) => setCorrectionText(e.target.value)}
              rows={3}
              className="w-full px-2 py-1 text-sm bg-surface-base border border-border-default rounded-comp text-text-primary resize-none"
            />
            <div className="flex gap-2 mt-1">
              <button
                onClick={() => { onCorrect(message.id, correctionText); setShowCorrection(false); }}
                disabled={!correctionText.trim()}
                className="px-2 py-1 text-xs bg-accent-brand text-white rounded-comp disabled:opacity-50"
              >
                Сохранить
              </button>
              <button
                onClick={() => setShowCorrection(false)}
                className="px-2 py-1 text-xs text-text-secondary hover:bg-surface-subtle rounded-comp"
              >
                Отмена
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
